import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Drawer,
    TextField,
    Tooltip,
    InputAdornment,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    IconButton,
    Toolbar,
    Typography,
    Button,
    ToggleButtonGroup,
    ToggleButton
} from '@mui/material';
import {
    Search as SearchIcon,
    FilterList as FilterListIcon,
    ViewModule as ViewModuleIcon,
    ViewList as ViewListIcon,
    Close as CloseIcon,
    CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { useDataProvider, useNotify } from 'react-admin';
import FolderTree from './FolderTree';
import FileGrid from './FileGrid';
import FileUploadZone from './FileUploadZone';
import CategoryManager from './CategoryManager';
import FileEditDialog from './FileEditDialog';
import FileMoveDialog from './FileMoveDialog';
import FileVersionDialog from './FileVersionDialog';

const DRAWER_WIDTH = 280;

const MediaLibrary: React.FC = () => {
    const [files, setFiles] = useState<any[]>([]);
    const [categories, setCategories] = useState<any[]>([]);
    const [selectedFolderId, setSelectedFolderId] = useState<number | undefined>();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [uploadZoneOpen, setUploadZoneOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<any | null>(null);
    const [moveDialogOpen, setMoveDialogOpen] = useState(false);
    const [versionDialogOpen, setVersionDialogOpen] = useState(false);

    const dataProvider = useDataProvider();
    const notify = useNotify();


    useEffect(() => {
        const init = async () => {
            await loadCategories();
            await loadFiles();
        };
        init();
    }, [selectedFolderId, searchQuery, selectedCategories]);

    const loadFiles = async () => {
        setLoading(true);
        try {
            const filter: any = {};

            if (selectedFolderId) {
                filter['folder.id'] = selectedFolderId;
            }

            if (searchQuery) {
                filter.originalFilename = searchQuery;
            }

            if (selectedCategories.length > 0) {
                filter['categories.id'] = selectedCategories;
            }

            const { data } = await dataProvider.getList('media_files', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'createdAt', order: 'DESC' },
                filter
            });

            // Resolve category IDs to full category objects
            // Load fresh categories for resolving
            const { data: currentCategories } = await dataProvider.getList('media_categories', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'name', order: 'ASC' },
                filter: {}
            });

            const filesWithResolvedCategories = data.map(file => {
                if (file.categories && Array.isArray(file.categories)) {
                    const resolvedCategories = file.categories.map((cat: any) => {
                        // Handle plain ID strings like "1", "2"
                        if (typeof cat === 'string' || typeof cat === 'number') {
                            const categoryId = typeof cat === 'string' ? parseInt(cat, 10) : cat;
                            return currentCategories.find(c => c.id === categoryId);
                        }
                        // Already an object
                        return cat;
                    }).filter(Boolean);
                    return { ...file, categories: resolvedCategories };
                }
                return file;
            });

            setFiles(filesWithResolvedCategories);
        } catch (error) {
            notify('Fehler beim Laden der Dateien', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        try {
            const { data } = await dataProvider.getList('media_categories', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'name', order: 'ASC' },
                filter: {}
            });
            setCategories(data);
        } catch (error) {
            notify('Fehler beim Laden der Kategorien', { type: 'error' });
        }
    };

    const handleFileDelete = async (fileId: number) => {
        if (window.confirm('Möchten Sie diese Datei wirklich löschen?')) {
            try {
                await dataProvider.delete('media_files', { id: fileId, previousData: { id: fileId } });
                notify('Datei gelöscht', { type: 'success' });
                loadFiles();
            } catch (error: any) {
                const detail = error?.body?.detail || error?.body?.['hydra:description'] || 'Fehler beim Löschen';
                notify(detail, { type: 'error' });
            }
        }
    };

    const handleFileEdit = (file: any) => {
        setSelectedFile(file);
        setEditDialogOpen(true);
    };

    const handleEditDialogClose = () => {
        setEditDialogOpen(false);
        setSelectedFile(null);
    };

    const handleCategoryToggle = (categoryId: number) => {
        setSelectedCategories(prev =>
            prev.includes(categoryId)
                ? prev.filter(id => id !== categoryId)
                : [...prev, categoryId]
        );
    };
    const handleFileMove = (file: any) => {
        setSelectedFile(file);
        setMoveDialogOpen(true);
    };

    const handleMoveDialogClose = () => {
        setMoveDialogOpen(false);
        setSelectedFile(null);
    };

    const handleFileVersion = (file: any) => {
        setSelectedFile(file);
        setVersionDialogOpen(true);
    };

    const handleVersionDialogClose = () => {
        setVersionDialogOpen(false);
        setSelectedFile(null);
    };

    return (
        // Auf schmalen Viewports stapeln sich Ordner-Sidebar und Inhalt
        // untereinander — die fixe 280px-Sidebar zog sonst die Seite ueber
        // die Viewport-Breite auf (#36-Sweep).
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, height: { md: 'calc(100vh - 64px)' } }}>
            {/* Linke Sidebar - Ordnerstruktur */}
            <Drawer
                variant="permanent"
                sx={{
                    width: { xs: '100%', md: DRAWER_WIDTH },
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: { xs: '100%', md: DRAWER_WIDTH },
                        boxSizing: 'border-box',
                        position: 'relative',
                        height: { xs: 'auto', md: '100%' },
                        maxHeight: { xs: 260, md: 'none' },
                        overflowY: 'auto',
                        borderRight: { xs: 'none', md: '1px solid' },
                        borderBottom: { xs: '1px solid', md: 'none' },
                        borderColor: { xs: 'divider', md: 'divider' }
                    }
                }}
            >
                <FolderTree
                    selectedFolderId={selectedFolderId}
                    onFolderSelect={setSelectedFolderId}
                />
            </Drawer>

            {/* Hauptbereich */}
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Toolbar */}
                <Paper sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}>
                    {/* flexWrap: die Toolbar (Suche, Kategorien, Upload, Ansicht) darf
                        auf schmalen Viewports umbrechen statt die Seite aufzuziehen (#36-Sweep) */}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                        <TextField
                            placeholder="Dateien suchen..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            size="small"
                            sx={{
                                flexGrow: 1,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '290486px',
                                    '&:hover fieldset': {
                                        borderColor: '#e72956'
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#e72956'
                                    }
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#1f3240' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchQuery && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchQuery('')}>
                                            <CloseIcon />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />

                        <CategoryManager
                            categories={categories}
                            onUpdate={loadCategories}
                        />

                        <Button
                            variant="contained"
                            startIcon={<CloudUploadIcon />}
                            onClick={() => setUploadZoneOpen(!uploadZoneOpen)}
                            sx={{
                                borderRadius: '290486px',
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 3
                            }}
                        >
                            Upload
                        </Button>

                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={(e, newMode) => newMode && setViewMode(newMode)}
                            size="small"
                            sx={{
                                '& .MuiToggleButton-root': {
                                    border: '1px solid #1f3240',
                                    color: '#1f3240',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        bgcolor: 'rgba(231, 41, 86, 0.08)',
                                        borderColor: '#e72956'
                                    },
                                    '&.Mui-selected': {
                                        bgcolor: '#e72956',
                                        color: 'white',
                                        borderColor: '#e72956',
                                        '&:hover': {
                                            bgcolor: '#c21642'
                                        }
                                    }
                                }
                            }}
                        >
                            <ToggleButton value="grid">
                                <Tooltip title="Kachelansicht"><ViewModuleIcon /></Tooltip>
                            </ToggleButton>
                            <ToggleButton value="list">
                                <Tooltip title="Listenansicht"><ViewListIcon /></Tooltip>
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {/* Kategorie-Filter */}
                    {categories.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <FilterListIcon fontSize="small" sx={{ color: '#1f3240' }} />
                            {categories.map((category) => (
                                <Chip
                                    key={category.id}
                                    label={category.name}
                                    onClick={() => handleCategoryToggle(category.id)}
                                    variant={selectedCategories.includes(category.id) ? 'filled' : 'outlined'}
                                    sx={{
                                        backgroundColor: selectedCategories.includes(category.id)
                                            ? category.color
                                            : 'transparent',
                                        color: selectedCategories.includes(category.id) ? 'white' : 'inherit',
                                        borderColor: category.color,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            transform: 'scale(1.05)',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                        }
                                    }}
                                    size="small"
                                />
                            ))}
                            {selectedCategories.length > 0 && (
                                <Button
                                    size="small"
                                    onClick={() => setSelectedCategories([])}
                                    sx={{
                                        ml: 1,
                                        borderRadius: '290486px',
                                        textTransform: 'none',
                                        color: '#e72956',
                                        '&:hover': {
                                            bgcolor: 'rgba(231, 41, 86, 0.08)'
                                        }
                                    }}
                                >
                                    Filter zurücksetzen
                                </Button>
                            )}
                        </Box>
                    )}
                </Paper>

                {/* Upload Zone (ausklappbar) */}
                {uploadZoneOpen && (
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <FileUploadZone
                            folderId={selectedFolderId}
                            onUploadComplete={() => {
                                loadFiles();
                                setUploadZoneOpen(false);
                            }}
                            categories={categories}
                        />
                    </Paper>
                )}

                {/* Datei-Grid */}
                <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
                    {loading ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography>Laden...</Typography>
                        </Box>
                    ) : (
                        <FileGrid
                            files={files}
                            onFileDelete={handleFileDelete}
                            onFileEdit={handleFileEdit}
                            onFileMove={handleFileMove}
                            onFileVersion={handleFileVersion}
                            viewMode={viewMode}
                        />
                    )}
                </Box>
            </Box>

            {/* File Edit Dialog */}
            <FileEditDialog
                open={editDialogOpen}
                file={selectedFile}
                onClose={handleEditDialogClose}
                onUpdate={loadFiles}
                allCategories={categories}
            />

            {/* File Move Dialog */}
            <FileMoveDialog
                open={moveDialogOpen}
                file={selectedFile}
                onClose={handleMoveDialogClose}
                onMove={loadFiles}
            />

            {/* File Version Dialog */}
            <FileVersionDialog
                open={versionDialogOpen}
                file={selectedFile}
                onClose={handleVersionDialogClose}
                onUpdate={loadFiles}
            />
        </Box>
    );
};

export default MediaLibrary;
